/**
 * Shared visual effects: custom cursor, reveal-on-scroll,
 * parallax, 3D tilt, marquee duplication.
 *
 * Call initEffects() in gatsby-browser.ts → onRouteUpdate
 * so effects re-attach after every client-side navigation.
 */

export function initEffects(): void {
  if (typeof document === "undefined") return;

  initCursor();
  initReveal();
  initParallax();
  initTilt();
  initMarquee();
}

// ---------- Cursor ----------

function initCursor(): void {
  // Remove stale elements from previous route
  document.querySelector(".cursor-dot")?.remove();
  document.querySelector(".cursor-ring")?.remove();

  const dot = document.createElement("div");
  dot.className = "cursor-dot";
  const ring = document.createElement("div");
  ring.className = "cursor-ring";
  document.body.appendChild(dot);
  document.body.appendChild(ring);

  // Switch to cursor:none only after elements are in the DOM
  document.body.classList.add("has-custom-cursor");

  let mx = window.innerWidth / 2;
  let my = window.innerHeight / 2;
  let rx = mx;
  let ry = my;

  window.addEventListener("mousemove", (e: MouseEvent) => {
    mx = e.clientX;
    my = e.clientY;
    dot.style.transform = `translate3d(${mx}px, ${my}px, 0) translate(-50%,-50%)`;
  });

  function loop(): void {
    rx += (mx - rx) * 0.18;
    ry += (my - ry) * 0.18;
    ring.style.transform = `translate3d(${rx}px, ${ry}px, 0) translate(-50%,-50%)`;
    requestAnimationFrame(loop);
  }
  loop();

  document.addEventListener("mouseover", (e: MouseEvent) => {
    if ((e.target as Element).closest("a, button, .tilt, [data-hover]")) {
      ring.classList.add("hovering");
    }
  });
  document.addEventListener("mouseout", (e: MouseEvent) => {
    if ((e.target as Element).closest("a, button, .tilt, [data-hover]")) {
      ring.classList.remove("hovering");
    }
  });
}

// ---------- Reveal on scroll ----------

function initReveal(): void {
  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((en) => {
        if (en.isIntersecting) {
          en.target.classList.add("in");
          io.unobserve(en.target);
        }
      });
    },
    { threshold: 0.12, rootMargin: "0px 0px -40px 0px" }
  );

  // Wrap .reveal-line text so the clip works correctly
  document.querySelectorAll<HTMLElement>(".reveal-line").forEach((el) => {
    if (el.dataset.wrapped) return;
    el.innerHTML = `<span>${el.innerHTML}</span>`;
    el.dataset.wrapped = "1";
  });

  document.querySelectorAll(".reveal, .reveal-line").forEach((el) => io.observe(el));

  // Stagger children of [data-stagger] containers
  document.querySelectorAll<HTMLElement>("[data-stagger]").forEach((group) => {
    group
      .querySelectorAll<HTMLElement>(".reveal, .reveal-line")
      .forEach((k, i) => {
        k.style.transitionDelay = `${i * 80}ms`;
      });
  });
}

// ---------- Parallax ----------

function initParallax(): void {
  const els = Array.from(document.querySelectorAll<HTMLElement>("[data-parallax]"));
  if (!els.length) return;

  function tick(): void {
    els.forEach((el) => {
      const speed = parseFloat(el.dataset.parallax ?? "0.2") || 0.2;
      const rect = el.getBoundingClientRect();
      const mid = rect.top + rect.height / 2;
      const off = (mid - window.innerHeight / 2) * speed;
      el.style.transform = `translate3d(0, ${-off}px, 0)`;
    });
    requestAnimationFrame(tick);
  }
  tick();
}

// ---------- 3D Tilt ----------

function initTilt(): void {
  document.querySelectorAll<HTMLElement>(".tilt").forEach((card) => {
    const inner = card.querySelector<HTMLElement>(".tilt-inner") ?? card;

    card.addEventListener("mousemove", (e: MouseEvent) => {
      const r = card.getBoundingClientRect();
      const px = (e.clientX - r.left) / r.width - 0.5;
      const py = (e.clientY - r.top) / r.height - 0.5;
      const max =
        parseFloat(
          getComputedStyle(document.documentElement).getPropertyValue("--tilt-deg")
        ) || 8;
      inner.style.transform = `perspective(900px) rotateY(${px * max}deg) rotateX(${-py * max}deg) translateZ(0)`;
      card.style.setProperty("--mx", `${px * 100 + 50}%`);
      card.style.setProperty("--my", `${py * 100 + 50}%`);
    });

    card.addEventListener("mouseleave", () => {
      inner.style.transform = "";
    });
  });
}

// ---------- Marquee duplicate ----------

function initMarquee(): void {
  document.querySelectorAll<HTMLElement>(".marquee-track").forEach((track) => {
    if (track.dataset.dup) return;
    track.innerHTML += track.innerHTML;
    track.dataset.dup = "1";
  });
}
